import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, IconButton, InputAdornment, ListItemIcon, ListItemText, Menu, MenuItem,
  Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlaceIcon from '@mui/icons-material/Place';
import ListAltIcon from '@mui/icons-material/ListAlt';
import MicIcon from '@mui/icons-material/Mic';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { Loader, NeedsHousehold, PageHeader, Panel, useLoad, useNotify } from '../common';
import { addDays, nearDay, quantity, searchKey, today } from '../labels';
import { GiftDialog } from './GiftDialog';
import { PrepareDialog } from './PrepareDialog';
import { RhythmDialog, rhythmText } from './RhythmDialog';
import { PlaceIcon as LookIcon, placeColors } from './placeLook';
import { PredictionDialog } from '../shopping/PredictionDialog';
import { markOut, putOnList, StockActionDeps } from './stockActions';

type Alert = 'out' | 'low' | 'soon' | 'list';

const NO_PLACE = 'sem-local';
const NO_ZONE = 'sem-zona';
/** "Acaba em breve": runs out within this many days. */
const SOON_DAYS = 7;

/** One tap of − / +: one unit, or 100 g / 100 ml, or half a kg / litre. */
function stockStep(unit: T.MeasureUnit): number {
  if (unit === 'Gram' || unit === 'Millilitre') return 100;
  if (unit === 'Kilogram' || unit === 'Litre') return 0.5;
  return 1;
}

/** "gasta ~1 L/dia", or null. */
function dailyUseText(item: T.StockItem): string | null {
  return item.dailyUse !== null && item.dailyUse !== undefined ? `gasta ~${quantity(item.dailyUse, item.unit)}/dia` : null;
}

/** Reserves, minimum, what the meals and the daily use will take, and the rhythm (the states go on chips). */
function stockDetail(item: T.StockItem): string {
  const parts: string[] = [];
  if (item.reserves.length > 0) {
    const atHand = item.mainQuantity !== null && item.mainQuantity !== undefined ? `${quantity(item.mainQuantity, item.unit)} à mão` : 'à mão';
    parts.push(`${atHand} · ${item.reserves.map(r => `${quantity(r.quantity, item.unit)} em ${r.locationName}`).join(', ')}`);
  }
  if (item.minStock !== null && item.minStock !== undefined) parts.push(`mínimo em casa ${quantity(item.minStock, item.unit)}`);
  if (item.packSize !== null && item.packSize !== undefined) parts.push(`embalagens de ${quantity(item.packSize, item.unit)}`);
  const daily = dailyUseText(item);
  if (daily) parts.push(daily);
  if (item.upcoming > 0) parts.push(`${daily ? 'até à próxima ida vai gastar' : 'as refeições vão gastar'} ${quantity(item.upcoming, item.unit)}`);
  const rhythm = rhythmText(item);
  if (rhythm) parts.push(rhythm);
  if (item.lastGiftFrom) parts.push(item.lastGiftOn ? `oferta de ${item.lastGiftFrom} (${nearDay(item.lastGiftOn)})` : `oferta de ${item.lastGiftFrom}`);
  return parts.join(' · ');
}

const counted = (i: T.StockItem) => i.quantity !== null && i.quantity !== undefined;
const isOut = (i: T.StockItem) => i.quantity === 0;
/** Below the minimum, or empty at hand with some kept elsewhere (and not out). */
const needsAttention = (i: T.StockItem) => !isOut(i) && (i.isLow || i.needsRestock);
const runsOutSoon = (i: T.StockItem) =>
  !isOut(i) && !needsAttention(i) && !!i.runsOutOn && i.runsOutOn <= addDays(today(), SOON_DAYS);

const inAlert = (i: T.StockItem, alert: Alert) =>
  alert === 'out' ? isOut(i) : alert === 'low' ? needsAttention(i) : alert === 'soon' ? runsOutSoon(i) : i.isOnList;

const ALERTS: { key: Alert; text: string; title: string; color: string; bg: string }[] = [
  { key: 'out', text: 'acabou', title: 'Acabou', color: 'error.dark', bg: 'rgba(211, 47, 47, 0.1)' },
  { key: 'low', text: 'baixo', title: 'Baixo', color: 'warning.dark', bg: 'rgba(237, 108, 2, 0.12)' },
  { key: 'soon', text: 'acaba esta semana', title: 'Acaba em breve', color: 'text.primary', bg: 'action.hover' },
  { key: 'list', text: 'na lista', title: 'Na lista', color: 'info.dark', bg: 'rgba(2, 136, 209, 0.1)' },
];

/** How full, 0 to 1, against twice the minimum; null without a count or a minimum. */
function level(item: T.StockItem): number | null {
  if (!counted(item) || !item.minStock || item.minStock <= 0) return null;
  return Math.min(1, (item.quantity as number) / (item.minStock * 2));
}

/** The one state worth saying on a tile, the most urgent first. */
function stateChip(item: T.StockItem, onRhythm: () => void): React.ReactNode {
  const list = item.isOnList ? ' · na lista' : '';
  if (!counted(item)) return <Chip size="small" label={`não contado${list}`} />;
  if (isOut(item)) return <Chip size="small" color="error" label={`acabou${list}`} />;
  if (item.needsRestock) return <Chip size="small" color="error" variant="outlined" label={`traz da reserva${list}`} />;
  if (item.isLow) return <Chip size="small" color="warning" variant="outlined" label={`baixo${list}`} />;
  if (runsOutSoon(item) && item.runsOutOn) return <Chip size="small" variant="outlined" label={`acaba ${nearDay(item.runsOutOn)}${list}`} />;
  if (item.isOnList) return <Chip size="small" color="info" variant="outlined" label="na lista" />;
  if (item.rhythmSuggestions) return <Chip size="small" variant="outlined" label="ajustar ao ritmo" onClick={e => { e.stopPropagation(); onRhythm(); }} />;
  return null;
}

function Stepper({ item, busy, onStep }: { item: T.StockItem; busy: boolean; onStep: (delta: number) => void }) {
  const step = stockStep(item.unit);
  return (
    <Stack direction="row" sx={{ alignItems: 'center', border: 1, borderColor: 'divider', borderRadius: 5, flexShrink: 0, bgcolor: 'background.paper', alignSelf: 'flex-start' }}>
      <IconButton size="small" color="primary" aria-label={`Menos ${item.name}`} disabled={busy} onClick={() => onStep(-step)}>
        <RemoveIcon fontSize="small" />
      </IconButton>
      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 36, textAlign: 'center', whiteSpace: 'nowrap' }}>
        {counted(item) ? quantity(item.quantity, item.unit) : '—'}
      </Typography>
      <IconButton size="small" color="primary" aria-label={`Mais ${item.name}`} disabled={busy} onClick={() => onStep(step)}>
        <AddIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
}

function LevelBar({ item, value }: { item: T.StockItem; value: number }) {
  return (
    <Box
      role="meter"
      aria-valuenow={Math.round(value * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`${item.name}: ${Math.round(value * 100)}% do nível bom`}
      sx={{ height: 5, borderRadius: 3, bgcolor: 'action.hover', overflow: 'hidden' }}
    >
      <Box sx={{ height: 5, width: `${value * 100}%`, bgcolor: isOut(item) ? 'error.main' : item.isLow ? 'warning.main' : 'success.main' }} />
    </Box>
  );
}

type ItemActions = {
  busy: string | null;
  onStep: (item: T.StockItem, delta: number) => void;
  onPrepare: (item: T.StockItem) => void;
  onRhythm: (item: T.StockItem) => void;
  onGift: (item: T.StockItem) => void;
  onOut: (item: T.StockItem) => void;
  onList: (item: T.StockItem) => void;
};

/** "⋮" on an item: "Acabou" when there is some, "Pôr na lista" when it is not there yet (as on Android). */
function ItemMenu({ item, actions }: { item: T.StockItem; actions: ItemActions }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const canOut = (item.quantity ?? 0) > 0;
  if (!canOut && item.isOnList) return null;
  const pick = (action: (item: T.StockItem) => void) => { setAnchor(null); action(item); };
  return (
    <>
      <IconButton size="small" aria-label={`Mais ações para ${item.name}`} disabled={actions.busy === item.catalogItemPublicId} onClick={e => setAnchor(e.currentTarget)}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
        {canOut && (
          <MenuItem onClick={() => pick(actions.onOut)}>
            <ListItemIcon><CloseIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Acabou" />
          </MenuItem>
        )}
        {!item.isOnList && (
          <MenuItem onClick={() => pick(actions.onList)}>
            <ListItemIcon><PlaylistAddIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Pôr na lista" />
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

/** An item on its shelf: name, state, how full, − n +; the name opens its editor. */
function Tile({ item, marked, actions }: { item: T.StockItem; marked: boolean; actions: ItemActions }) {
  const navigate = useNavigate();
  const bar = level(item);
  return (
    <Box
      data-item={item.catalogItemPublicId}
      sx={{
        border: marked ? 2 : 1, borderColor: marked ? 'primary.main' : 'divider', borderRadius: 3, p: 1.25,
        bgcolor: isOut(item) ? 'rgba(211, 47, 47, 0.06)' : 'background.paper', display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0,
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={() => navigate(item.catalogItemPublicId, { state: { item } })}
        onKeyDown={e => { if (e.key === 'Enter') navigate(item.catalogItemPublicId, { state: { item } }); }}
        sx={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: 14, overflowWrap: 'anywhere', lineHeight: 1.3 }}>{item.name}</Typography>
        <Box>{stateChip(item, () => actions.onRhythm(item))}</Box>
        {bar !== null && <LevelBar item={item} value={bar} />}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Stepper item={item} busy={actions.busy === item.catalogItemPublicId} onStep={delta => actions.onStep(item, delta)} />
        <Box sx={{ flexGrow: 1 }} />
        {(item.preparedFromPublicId || item.preparedItems.length > 0) && (
          <Tooltip title="Preparar">
            <IconButton size="small" color="primary" aria-label={`Preparar ${item.name}`} onClick={() => actions.onPrepare(item)}><ContentCutIcon fontSize="small" /></IconButton>
          </Tooltip>
        )}
        <Tooltip title="Recebi de oferta">
          <IconButton size="small" aria-label={`Recebi ${item.name} de oferta`} onClick={() => actions.onGift(item)}><CardGiftcardIcon fontSize="small" /></IconButton>
        </Tooltip>
        <ItemMenu item={item} actions={actions} />
      </Box>
    </Box>
  );
}

/** Part of an item kept here away from its main place; the name opens its editor, "⋮" the item's actions. */
function ReserveTile({ item, reserve, actions }: { item: T.StockItem; reserve: T.StockReserve; actions: ItemActions }) {
  const navigate = useNavigate();
  return (
    <Box sx={{ border: 1, borderStyle: 'dashed', borderColor: 'divider', borderRadius: 3, p: 1.25, display: 'flex', alignItems: 'flex-start', gap: 0.5, minWidth: 0 }}>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => navigate(item.catalogItemPublicId, { state: { item } })}
        onKeyDown={e => { if (e.key === 'Enter') navigate(item.catalogItemPublicId, { state: { item } }); }}
        sx={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0, flexGrow: 1 }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: 14, overflowWrap: 'anywhere' }}>{item.name}</Typography>
        <Box><Chip size="small" label="reserva" /></Box>
        <Typography variant="body2">{quantity(reserve.quantity, item.unit)} aqui</Typography>
        {item.locationName && <Typography variant="caption" color="text.secondary">o resto em {item.locationName}</Typography>}
      </Box>
      <ItemMenu item={item} actions={actions} />
    </Box>
  );
}

/** Stock at home as a map of places, each place's zones as shelves, and the alerts on top. */
const StockList: React.FC = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [params, setParams] = useSearchParams();
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [preparing, setPreparing] = useState<T.StockItem | null>(null);
  const [adjusting, setAdjusting] = useState<T.StockItem | null>(null);
  /** "Recebi de oferta": open for an item, or for any (`null` item) from the menu. */
  const [gift, setGift] = useState<{ item: T.StockItem | null } | null>(null);
  const [predicting, setPredicting] = useState<T.ShoppingPrediction | null>(null);
  const { data, setData, error, loading, reload } = useLoad(() => Promise.all([api.getStock(), api.getStorageLocations()]));
  const shelvesRef = useRef<HTMLDivElement | null>(null);

  const search = params.get('q') ?? '';
  const placeParam = params.get('local');
  const alertParam = params.get('alerta') as Alert | null;
  const marked = params.get('ver');

  const all = useMemo(() => data?.[0] ?? [], [data]);
  const locations = useMemo(() => data?.[1] ?? [], [data]);
  const byId = useMemo(() => new Map(locations.map(l => [l.publicId, l])), [locations]);
  const places = locations.filter(l => !l.parentPublicId);

  const placeOf = (locationId?: T.Guid | null): T.StorageLocation | undefined => {
    const location = locationId ? byId.get(locationId) : undefined;
    return location?.parentPublicId ? byId.get(location.parentPublicId) ?? location : location;
  };
  const placeKey = (locationId?: T.Guid | null) => placeOf(locationId)?.publicId ?? NO_PLACE;
  const zoneKey = (locationId?: T.Guid | null) => {
    const location = locationId ? byId.get(locationId) : undefined;
    return location?.parentPublicId ? location.publicId : NO_ZONE;
  };
  /** The items kept at a place, as their main place or a reserve. */
  const itemsAt = (place: string) => [
    ...all.filter(i => placeKey(i.locationPublicId) === place).map(item => ({ item, reserve: null as T.StockReserve | null })),
    ...all.flatMap(item => item.reserves.filter(r => placeKey(r.locationPublicId) === place).map(reserve => ({ item, reserve: reserve as T.StockReserve | null }))),
  ];

  const go = (next: { local?: string | null; alerta?: string | null; q?: string | null; ver?: string | null }) => {
    const query = new URLSearchParams();
    Object.entries(next).forEach(([k, v]) => { if (v) query.set(k, v); });
    setParams(query, { replace: !!next.q || (!next.local && !next.alerta) });
  };

  // An item asked for: scroll to it on its shelf
  useEffect(() => {
    if (!marked || !placeParam || !shelvesRef.current) return;
    const tile = shelvesRef.current.querySelector(`[data-item="${marked}"]`);
    if (tile) tile.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [marked, placeParam, data]);

  const replace = (updated: T.StockItem) =>
    setData(current => current && [current[0].map(i => (i.catalogItemPublicId === updated.catalogItemPublicId ? updated : i)), current[1]]);

  const adjust = async (item: T.StockItem, delta: number) => {
    setBusy(item.catalogItemPublicId);
    try {
      replace(await api.adjustStock(item.catalogItemPublicId, delta));
    } catch (e: any) {
      notify(e.message, { severity: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const stockActionDeps: StockActionDeps = {
    adjustStock: api.adjustStock,
    addShoppingNeed: api.addShoppingNeed,
    replace,
    notify,
    predict: setPredicting,
  };

  /** Keeps the item busy while the call runs, as the stepper does. */
  const withBusy = (action: (item: T.StockItem, deps: StockActionDeps) => Promise<void>) => async (item: T.StockItem) => {
    setBusy(item.catalogItemPublicId);
    try {
      await action(item, stockActionDeps);
    } finally {
      setBusy(null);
    }
  };

  const actions: ItemActions = {
    busy,
    onStep: adjust,
    onPrepare: setPreparing,
    onRhythm: setAdjusting,
    onGift: item => setGift({ item }),
    onOut: withBusy(markOut),
    onList: withBusy(putOnList),
  };

  const searching = searchKey(search).length > 0;
  const openPlace = placeParam && !searching ? (placeParam === NO_PLACE ? NO_PLACE : byId.has(placeParam) ? placeParam : null) : null;
  const alert = !openPlace && !searching && alertParam && ALERTS.some(a => a.key === alertParam) ? alertParam : null;

  const alertCounts = ALERTS.map(a => ({ ...a, count: all.filter(i => inAlert(i, a.key)).length })).filter(a => a.count > 0);
  const allGood = alertCounts.every(a => a.key === 'list');

  /** "▣ Despensa › Armário de cima": where an item is, in its place's colour; opens the place with it marked. */
  const whereabouts = (item: T.StockItem) => {
    const place = placeOf(item.locationPublicId);
    const colors = placeColors(place?.color);
    const text = (item.locationName ?? 'Sem local') + (item.reserves.length > 0 ? ` · e ${item.reserves.map(r => r.locationName).join(', ')}` : '');
    return (
      <Box
        component="button"
        onClick={() => go({ local: placeKey(item.locationPublicId), ver: item.catalogItemPublicId })}
        aria-label={`Ver ${item.name} em ${item.locationName ?? 'sem local'}`}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, border: 0, bgcolor: colors.soft, color: colors.text, borderRadius: 2, px: 1, py: 0.25, cursor: 'pointer', font: 'inherit', fontSize: 12, mt: 0.5 }}
      >
        <LookIcon icon={place?.icon ?? 'box'} color={place?.color ?? 'gray'} sx={{ fontSize: 14 }} />
        {text}
      </Box>
    );
  };

  const listRow = (item: T.StockItem) => {
    const detail = stockDetail(item);
    return (
      <Box key={item.catalogItemPublicId} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, minHeight: 52, borderTop: 1, borderColor: 'divider' }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Box
            role="button"
            tabIndex={0}
            onClick={() => navigate(item.catalogItemPublicId, { state: { item } })}
            onKeyDown={e => { if (e.key === 'Enter') navigate(item.catalogItemPublicId, { state: { item } }); }}
            sx={{ cursor: 'pointer' }}
          >
            <Typography sx={{ overflowWrap: 'anywhere', fontWeight: 550 }}>{item.name}</Typography>
            <Box sx={{ my: 0.25 }}>{stateChip(item, () => setAdjusting(item))}</Box>
            {detail && <Typography variant="caption" color="text.secondary" component="div">{detail}</Typography>}
          </Box>
          {whereabouts(item)}
        </Box>
        <Stepper item={item} busy={busy === item.catalogItemPublicId} onStep={delta => adjust(item, delta)} />
        <ItemMenu item={item} actions={actions} />
      </Box>
    );
  };

  const placeCard = (key: string, name: string, icon: string, color: string, frozen: number) => {
    const here = itemsAt(key);
    const main = here.filter(h => !h.reserve).map(h => h.item);
    const count = new Set(here.map(h => h.item.catalogItemPublicId)).size;
    const outCount = main.filter(isOut).length;
    const lowCount = main.filter(needsAttention).length;
    const colors = placeColors(color);
    const parts = [count === 0 ? 'vazio' : count === 1 ? '1 artigo' : `${count} artigos`];
    if (outCount > 0) parts.push(outCount === 1 ? '1 acabou' : `${outCount} acabaram`);
    if (lowCount > 0) parts.push(lowCount === 1 ? '1 baixo' : `${lowCount} baixos`);
    if (frozen > 0) parts.push(frozen === 1 ? '1 congelado' : `${frozen} congelados`);
    const glimpse = Array.from(new Map(here.map(h => [h.item.catalogItemPublicId, h.item])).values())
      .sort((a, b) => (isOut(a) ? 0 : needsAttention(a) ? 1 : 2) - (isOut(b) ? 0 : needsAttention(b) ? 1 : 2) || a.name.localeCompare(b.name, 'pt-PT'))
      .slice(0, 4).map(i => i.name);
    return (
      <Box
        key={key}
        component="button"
        onClick={() => go({ local: key })}
        aria-label={`${name}: ${parts.join(', ')}`}
        sx={{
          textAlign: 'left', font: 'inherit', cursor: 'pointer', bgcolor: colors.soft, color: colors.text, borderRadius: 3.5, p: 1.75, minHeight: 132,
          border: key === NO_PLACE ? `1.5px dashed ${colors.strong}` : '1.5px solid transparent', display: 'flex', flexDirection: 'column', gap: 0.25,
          transition: 'border-color .15s', '&:hover': { borderColor: colors.strong },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
          <LookIcon icon={icon} color={color} sx={{ fontSize: 30 }} />
          <Box sx={{ flexGrow: 1 }} />
          {(outCount > 0 || lowCount > 0) && (
            <Box sx={{ minWidth: 22, height: 22, px: 0.75, borderRadius: 11, bgcolor: outCount > 0 ? 'error.main' : 'warning.main', color: '#fff', fontSize: 12, fontWeight: 650, display: 'grid', placeItems: 'center' }}>
              {outCount > 0 ? outCount : lowCount}
            </Box>
          )}
        </Box>
        <Typography sx={{ fontWeight: 650, fontSize: 16, mt: 0.75, color: colors.text }}>{name}</Typography>
        <Typography sx={{ fontSize: 12.5, color: colors.strong }}>{parts.join(' · ')}</Typography>
        {glimpse.length > 0 && (
          <Typography sx={{ fontSize: 12.5, color: colors.text, mt: 0.25, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {glimpse.join(', ')}{count > glimpse.length ? '…' : ''}
          </Typography>
        )}
      </Box>
    );
  };

  const renderMap = () => (
    <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(auto-fill, minmax(220px, 1fr))' } }}>
      {places.map(p => placeCard(p.publicId, p.name, p.icon, p.color,
        p.isFreezer ? locations.filter(z => z.parentPublicId === p.publicId).reduce((sum, z) => sum + z.frozenCount, p.frozenCount) : 0))}
      {all.some(i => placeKey(i.locationPublicId) === NO_PLACE) && placeCard(NO_PLACE, 'Sem local', 'box', 'gray', 0)}
    </Box>
  );

  const renderPlace = (key: string) => {
    const place = key === NO_PLACE ? undefined : byId.get(key);
    const colors = placeColors(place?.color);
    const here = itemsAt(key);
    const zones = place ? locations.filter(l => l.parentPublicId === place.publicId) : [];
    const shelfOf = (h: { item: T.StockItem; reserve: T.StockReserve | null }) => zoneKey(h.reserve?.locationPublicId ?? h.item.locationPublicId);
    const loose = here.filter(h => shelfOf(h) === NO_ZONE);
    const shelves = [
      ...(loose.length > 0 || zones.length === 0 ? [{ key: NO_ZONE, name: zones.length === 0 ? place?.name ?? 'Por arrumar' : 'Sem zona', items: loose }] : []),
      ...zones.map(z => ({ key: z.publicId, name: z.name, items: here.filter(h => shelfOf(h) === z.publicId) })),
    ];
    const count = new Set(here.map(h => h.item.catalogItemPublicId)).size;
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, bgcolor: colors.soft, color: colors.text, borderRadius: 3.5, p: 1, pr: 2, mb: 1.5 }}>
          <IconButton aria-label="Voltar ao mapa da casa" onClick={() => go({})} sx={{ color: colors.text }}><ArrowBackIcon /></IconButton>
          <LookIcon icon={place?.icon ?? 'box'} color={place?.color ?? 'gray'} sx={{ fontSize: 30 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 650, fontSize: 18, color: colors.text }}>{place?.name ?? 'Sem local'}</Typography>
            <Typography sx={{ fontSize: 12.5, color: colors.strong }}>Em casa › {place?.name ?? 'Sem local'} · {count === 1 ? '1 artigo' : `${count} artigos`}</Typography>
          </Box>
        </Box>
        {shelves.length > 1 && (
          <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1, mb: 1 }}>
            {shelves.map(s => (
              <Chip
                key={s.key}
                label={s.items.length > 0 ? `${s.name} · ${s.items.length}` : s.name}
                variant="outlined"
                onClick={() => document.getElementById(`shelf-${s.key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              />
            ))}
          </Box>
        )}
        <Box ref={shelvesRef} sx={{ border: 2, borderColor: colors.strong, borderRadius: 3.5, p: { xs: 1.25, sm: 2 }, pb: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {shelves.map(s => (
            <Box key={s.key} id={`shelf-${s.key}`} sx={{ scrollMarginTop: 80 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
                <Typography sx={{ fontWeight: 650, fontSize: 14, color: colors.text, flexGrow: 1 }}>{s.name}</Typography>
                <Typography variant="caption" color="text.secondary">{s.items.length === 0 ? 'vazia' : s.items.length}</Typography>
              </Box>
              {s.items.length > 0 && (
                <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(auto-fill, minmax(190px, 1fr))' } }}>
                  {[...s.items]
                    .sort((a, b) => (a.reserve ? 1 : 0) - (b.reserve ? 1 : 0) || a.item.name.localeCompare(b.item.name, 'pt-PT'))
                    .map(h => (h.reserve
                      ? <ReserveTile key={`${h.item.catalogItemPublicId}-${h.reserve.locationPublicId}`} item={h.item} reserve={h.reserve} actions={actions} />
                      : <Tile key={h.item.catalogItemPublicId} item={h.item} marked={marked === h.item.catalogItemPublicId} actions={actions} />))}
                </Box>
              )}
              {/* The shelf board */}
              <Box sx={{ height: 4, borderRadius: 2, bgcolor: colors.strong, mt: 1 }} />
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const renderList = () => {
    const key = searchKey(search);
    const order = new Map(places.map((p, i) => [p.publicId, i]));
    const list = all
      .filter(i => (searching ? searchKey(i.name).includes(key) : inAlert(i, alert as Alert)))
      .sort((a, b) => (order.get(placeKey(a.locationPublicId)) ?? 999) - (order.get(placeKey(b.locationPublicId)) ?? 999) || a.name.localeCompare(b.name, 'pt-PT'));
    const title = searching ? `«${search.trim()}»` : ALERTS.find(a => a.key === alert)?.title;
    return (
      <Panel title={title} action={<Button size="small" onClick={() => go({})}>Mapa</Button>} sx={{ pt: 0.5 }}>
        {list.length === 0
          ? <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>Nada aqui.</Typography>
          : list.map(listRow)}
      </Panel>
    );
  };

  return (
    <Box>
      <PageHeader
        eyebrow="Compras"
        title="Em casa"
        subtitle={all.length > 0 ? `${all.length} artigos em ${places.length} locais` : undefined}
        actions={(
          <>
            <Tooltip title="Atualizar"><IconButton onClick={reload}><RefreshIcon /></IconButton></Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} component={RouterLink} to="novo">Juntar</Button>
            <Tooltip title="Mais opções"><IconButton onClick={e => setMenu(e.currentTarget)}><MoreVertIcon /></IconButton></Tooltip>
          </>
        )}
      />
      <Menu anchorEl={menu} open={menu !== null} onClose={() => setMenu(null)}>
        <MenuItem onClick={() => { setMenu(null); setGift({ item: null }); }}>
          <ListItemIcon><CardGiftcardIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Recebi de oferta" secondary="Algo que te deram entra em casa e sai da lista" />
        </MenuItem>
        <MenuItem onClick={() => navigate('locais')}>
          <ListItemIcon><PlaceIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Locais e zonas" secondary="Nomes, ícones e cores, ordem, apagar" />
        </MenuItem>
        <MenuItem onClick={() => navigate('/casa/catalogo')}>
          <ListItemIcon><ListAltIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Catálogo" secondary="Todos os artigos, nomes e duplicados" />
        </MenuItem>
      </Menu>

      <Loader loading={loading} error={error} onRetry={reload} hasData={data !== undefined}>
        {all.length === 0 ? (
          <Card variant="outlined" sx={{ my: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Ainda não estás a contar nada</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Diz o que tens e onde está; as refeições gastam sozinhas, as compras pagas somam e o que fica abaixo do mínimo vai para a lista.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                «tenho 12 ovos» · «o arroz está no armário de cima da despensa» · «quero ter sempre 6 ovos»
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <Button variant="contained" startIcon={<MicIcon />} component={RouterLink} to="/casa/dizer">Dizer</Button>
                <Button variant="outlined" startIcon={<AddIcon />} component={RouterLink} to="novo">Juntar</Button>
              </Stack>
            </CardContent>
          </Card>
        ) : (
          <>
            {!openPlace && (
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2, alignItems: { md: 'stretch' } }}>
                <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: `repeat(${alertCounts.length + (allGood ? 1 : 0)}, minmax(0, 1fr))`, flexGrow: 1 }}>
                  {allGood ? (
                    <Box sx={{ borderRadius: 3, bgcolor: 'rgba(46, 125, 50, 0.1)', color: 'success.dark', display: 'grid', placeItems: 'center', p: 1.5, fontWeight: 600 }}>Tudo em ordem</Box>
                  ) : null}
                  {alertCounts.map(a => (
                    <Box
                      key={a.key}
                      component="button"
                      aria-pressed={alert === a.key}
                      aria-label={`${a.count} ${a.text}: ver quais e onde estão`}
                      onClick={() => go(alert === a.key ? {} : { alerta: a.key })}
                      sx={{ font: 'inherit', cursor: 'pointer', borderRadius: 3, bgcolor: a.bg, color: a.color, border: 2, borderColor: alert === a.key ? 'currentColor' : 'transparent', p: 1, textAlign: 'center' }}
                    >
                      <Box sx={{ fontSize: 22, fontWeight: 650, lineHeight: 1.2 }}>{a.count}</Box>
                      <Box sx={{ fontSize: 12.5 }}>{a.text}</Box>
                    </Box>
                  ))}
                </Box>
                <TextField
                  size="small"
                  placeholder="Onde está…?"
                  value={search}
                  onChange={e => go({ q: e.target.value })}
                  sx={{ minWidth: { md: 320 }, alignSelf: { md: 'center' } }}
                  slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
                />
              </Stack>
            )}
            {openPlace ? renderPlace(openPlace) : searching || alert ? renderList() : renderMap()}
          </>
        )}
      </Loader>
      <GiftDialog
        open={gift !== null}
        item={gift?.item}
        onClose={() => setGift(null)}
        onDone={async (_, message) => { setGift(null); notify(message, { severity: 'success' }); await reload(); }}
      />
      <PredictionDialog prediction={predicting} onClose={() => setPredicting(null)} />
      <RhythmDialog item={adjusting} onClose={updated => { setAdjusting(null); if (updated) replace(updated); }} />
      <PrepareDialog
        itemId={preparing?.catalogItemPublicId ?? null}
        onClose={() => setPreparing(null)}
        onDone={async message => { setPreparing(null); notify(message, { severity: 'success' }); await reload(); }}
      />
    </Box>
  );
};

const StockPage: React.FC = () => <NeedsHousehold><StockList /></NeedsHousehold>;

export default StockPage;
